// frontend/src/pages/Portal/SuperAdmin/Clinics/SuperAdminClinicDetailPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./SuperAdminClinicDetailPage.css";

type ClinicPublic = {
  ClinicID: number | string;
  Name: string | null;

  AddressLine1?: string | null;
  AddressLine2?: string | null;
  PostalCode?: string | null;
  Province?: string | null;

  Phone?: string | null;
  PhoneExtension?: string | null;
  Fax?: string | null;
  AdministrationEmail?: string | null;
  Website?: string | null;

  ClinicSettingID?: number | null;
  ClinicSettingName?: string | null;

  Latitude?: number | string | null;
  Longitude?: number | string | null;

  CityID?: number | null;
  CityName?: string | null;

  CityRegionID?: number | null;
  CityRegionName?: string | null;

  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

type SuperAdminClinic = {
  ClinicID: number | string;
  ClinicName?: string | null;
  Name?: string | null;

  GovernanceStatus?: string | null;
  ApprovedAt?: string | null;
  ApprovedByUserID?: number | string | null;

  AddressLine1?: string | null;
  AddressLine2?: string | null;
  PostalCode?: string | null;
  Province?: string | null;

  Phone?: string | null;
  PhoneExtension?: string | null;
  Fax?: string | null;
  AdministrationEmail?: string | null;
  Website?: string | null;

  Latitude?: number | string | null;
  Longitude?: number | string | null;

  CityID?: number | null;
  CityName?: string | null;

  CityRegionID?: number | null;
  CityRegionName?: string | null;
  RegionID?: number | null;
  RegionName?: string | null;

  CreatedAt?: string | null;
  UpdatedAt?: string | null;
  ActiveClinicAdminCount?: number | string | null;

  [key: string]: unknown;
};

type ClinicAdminRow = {
  UserRoleID?: number | string | null;
  UserID?: number | string | null;
  DisplayName?: string | null;
  Email?: string | null;
  RoleName?: string | null;
  IsActive?: boolean | number | string | null;
  AssignedAt?: string | null;
  DeactivatedAt?: string | null;
  DeactivatedByUserID?: number | string | null;
  UserStatus?: string | null;
  AuthProvider?: string | null;
  [key: string]: unknown;
};

type ClinicsPublicDetailResponse = {
  clinic?: ClinicPublic | null;
  accessibilityFeatures?: unknown[];
  onSiteServices?: unknown[];
  operatingHours?: unknown[];
  regionMappings?: unknown[];
};

type SuperAdminClinicResponse = {
  clinic?: SuperAdminClinic | null;
  admins?: ClinicAdminRow[];
  clinicAdmins?: ClinicAdminRow[];
  error?: string;
};

type BadgeTone = "success" | "danger" | "warning" | "neutral" | "info";

type PublicEligibilityResult = {
  known: boolean;
  eligible: boolean;
  missing: string[];
};


type ClinicEditForm = {
  name: string;
  addressLine1: string;
  addressLine2: string;
  province: string;
  postalCode: string;
  phone: string;
  phoneExtension: string;
  fax: string;
  administrationEmail: string;
  website: string;
  latitude: string;
  longitude: string;
};

export default function SuperAdminClinicDetailPage() {
  const nav = useNavigate();
  const params = useParams();
  const clinicIdNormalized = String(params.clinicId ?? "").trim();

  const [clinic, setClinic] = useState<ClinicPublic | null>(null);
  const [superAdminClinic, setSuperAdminClinic] =
    useState<SuperAdminClinic | null>(null);

  const [accessibilityFeatures, setAccessibilityFeatures] = useState<unknown[]>(
    []
  );
  const [onSiteServices, setOnSiteServices] = useState<unknown[]>([]);
  const [operatingHours, setOperatingHours] = useState<unknown[]>([]);
  const [regionMappings, setRegionMappings] = useState<unknown[]>([]);

  const [governanceStatus, setGovernanceStatus] = useState<string | null>(null);
  const [admins, setAdmins] = useState<ClinicAdminRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [publicErr, setPublicErr] = useState<string | null>(null);
  const [govLoading, setGovLoading] = useState(true);
  const [govErr, setGovErr] = useState<string | null>(null);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [adminsErr, setAdminsErr] = useState<string | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);

  const [isEditingClinic, setIsEditingClinic] = useState(false);
  const [clinicSaving, setClinicSaving] = useState(false);
  const [clinicForm, setClinicForm] = useState<ClinicEditForm>(() =>
    buildClinicEditForm(null, null)
  );

  const displayClinicId =
    superAdminClinic?.ClinicID ?? clinic?.ClinicID ?? clinicIdNormalized;

  const displayClinicName =
    superAdminClinic?.ClinicName || superAdminClinic?.Name || clinic?.Name || "—";

  const displayCityName =
    superAdminClinic?.CityName || clinic?.CityName || "City not set";

  const displayRegionName =
    superAdminClinic?.RegionName ||
    superAdminClinic?.CityRegionName ||
    clinic?.CityRegionName ||
    "";

  const publicEligibility = useMemo(
    () => getPublicEligibility(superAdminClinic || clinic),
    [clinic, superAdminClinic]
  );

  const activeAdminCount = useMemo(() => {
    const explicit = asNumberOrNull(
      getFirstValue(superAdminClinic || {}, [
        "ActiveClinicAdminCount",
        "activeClinicAdminCount",
        "ClinicAdminCount",
        "clinicAdminCount",
        "ActiveAdminCount",
        "activeAdminCount",
      ])
    );

    if (explicit !== null) return explicit;

    const activeRows = admins.filter((a) => isRoleActive(a.IsActive));
    return activeRows.length;
  }, [admins, superAdminClinic]);

  const approvedBy = formatNullable(
    superAdminClinic?.ApprovedByUserID ??
      getFirstValue(superAdminClinic || {}, ["approvedByUserID", "ApprovedBy"])
  );

  const lifecycleMeta = [
    superAdminClinic?.CreatedAt || clinic?.CreatedAt
      ? `Created: ${fmtDate(superAdminClinic?.CreatedAt || clinic?.CreatedAt)}`
      : null,
    superAdminClinic?.UpdatedAt || clinic?.UpdatedAt
      ? `Updated: ${fmtDate(superAdminClinic?.UpdatedAt || clinic?.UpdatedAt)}`
      : null,
    superAdminClinic?.ApprovedAt
      ? `Approved: ${fmtDate(superAdminClinic.ApprovedAt)}`
      : null,
    approvedBy !== "—" ? `Approved by UserID: ${approvedBy}` : null,
  ].filter(Boolean);

  const governanceStatusNorm = String(governanceStatus || "")
    .trim()
    .toLowerCase();
  const canSuspendClinic = governanceStatusNorm === "approved";
  const canActivateClinic = governanceStatusNorm === "suspended";

  // 1) Public/detail shape: clinic profile fields + child lists.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!clinicIdNormalized) return;

      setLoading(true);
      setPublicErr(null);

      try {
        const url = `/api/clinics?clinicId=${encodeURIComponent(
          clinicIdNormalized
        )}`;
        const res = await fetch(url, { credentials: "include" });

        if (!res.ok) {
          let msg = `Failed to load clinic details (${res.status})`;
          try {
            const j = await res.json();
            if (j?.error) msg = j.error;
          } catch {
            // ignore
          }
          throw new Error(msg);
        }

        const data = (await res.json()) as ClinicsPublicDetailResponse;
        const c = data?.clinic || null;
        if (!c) throw new Error("Public clinic details not found");

        if (cancelled) return;

        setClinic(c);
        setAccessibilityFeatures(
          Array.isArray(data?.accessibilityFeatures)
            ? data.accessibilityFeatures
            : []
        );
        setOnSiteServices(
          Array.isArray(data?.onSiteServices) ? data.onSiteServices : []
        );
        setOperatingHours(
          Array.isArray(data?.operatingHours) ? data.operatingHours : []
        );
        setRegionMappings(
          Array.isArray(data?.regionMappings) ? data.regionMappings : []
        );
      } catch {
        if (!cancelled) {
          // The public clinic endpoint intentionally hides clinics that are not
          // public-search eligible yet. This Super Admin page must still load
          // from the Super Admin governance endpoint, so public-detail failure
          // is treated as missing supplemental public metadata only.
          setPublicErr(null);
          setAccessibilityFeatures([]);
          setOnSiteServices([]);
          setOperatingHours([]);
          setRegionMappings([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [clinicIdNormalized]);

  // 2) SuperAdmin detail: governance, admins, and public listing fields.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!clinicIdNormalized) return;

      setGovLoading(true);
      setGovErr(null);
      setGovernanceStatus(null);
      setSuperAdminClinic(null);

      setAdminsLoading(true);
      setAdminsErr(null);
      setAdmins([]);

      try {
        const url = `/api/superadmin-clinics-list?clinicId=${encodeURIComponent(
          clinicIdNormalized
        )}`;
        const res = await fetch(url, { credentials: "include" });

        const data = (await res.json().catch(() => ({}))) as SuperAdminClinicResponse;

        if (!res.ok) {
          throw new Error(
            data?.error || `Failed to load governance details (${res.status})`
          );
        }

        if (cancelled) return;

        const c = data?.clinic || null;
        setSuperAdminClinic(c);
        setGovernanceStatus(
          String(c?.GovernanceStatus || getFirstValue(c || {}, ["governanceStatus"]) || "").trim() ||
            null
        );

        const adminsList = Array.isArray(data?.admins)
          ? data.admins
          : Array.isArray(data?.clinicAdmins)
            ? data.clinicAdmins
            : [];

        setAdmins(adminsList);
      } catch (e: any) {
        if (!cancelled) {
          setGovErr(e?.message || "Failed to load governance details");
          setAdminsErr(e?.message || "Failed to load Clinic Admins");
        }
      } finally {
        if (!cancelled) {
          setGovLoading(false);
          setAdminsLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [clinicIdNormalized]);

  useEffect(() => {
    if (isEditingClinic || clinicSaving) return;
    setClinicForm(buildClinicEditForm(superAdminClinic, clinic));
  }, [clinic, clinicSaving, isEditingClinic, superAdminClinic]);

  function reloadPage() {
    window.location.reload();
  }

  function backToClinics() {
    nav("/portal/super-admin/clinics");
  }

  function startClinicEdit() {
    setClinicForm(buildClinicEditForm(superAdminClinic, clinic));
    setIsEditingClinic(true);
  }

  function cancelClinicEdit() {
    setClinicForm(buildClinicEditForm(superAdminClinic, clinic));
    setIsEditingClinic(false);
  }

  function updateClinicField(field: keyof ClinicEditForm, value: string) {
    setClinicForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function saveClinicProfile() {
    if (clinicSaving) return;

    const clinicId = displayClinicId;
    if (!clinicId) {
      window.alert("ClinicID is missing.");
      return;
    }

    const cleanName = clinicForm.name.trim();
    if (!cleanName) {
      window.alert("Clinic name cannot be blank.");
      return;
    }

    const reason = window.prompt(
      "Enter Super Admin update reason:",
      "Clinic profile updated by Super Admin"
    );

    if (reason === null) return;

    const cleanReason = reason.trim();
    if (cleanReason.length < 5) {
      window.alert("Update reason must be at least 5 characters.");
      return;
    }

    setClinicSaving(true);

    try {
      const res = await fetch("/api/superadmin-clinic-update", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          clinicId,
          reason: cleanReason,
          name: cleanName,
          addressLine1: cleanNullable(clinicForm.addressLine1),
          addressLine2: cleanNullable(clinicForm.addressLine2),
          province: cleanNullable(clinicForm.province),
          postalCode: cleanNullable(clinicForm.postalCode),
          phone: cleanNullable(clinicForm.phone),
          phoneExtension: cleanNullable(clinicForm.phoneExtension),
          fax: cleanNullable(clinicForm.fax),
          administrationEmail: cleanNullable(clinicForm.administrationEmail),
          website: cleanNullable(clinicForm.website),
          latitude: cleanNullable(clinicForm.latitude),
          longitude: cleanNullable(clinicForm.longitude),
        }),
      });

      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        clinic?: SuperAdminClinic | null;
      };

      if (!res.ok) {
        throw new Error(data?.error || `Failed to update clinic (${res.status})`);
      }

      const updatedClinic = data?.clinic || null;
      if (updatedClinic) {
        setSuperAdminClinic((prev) => ({
          ...(prev || {}),
          ...updatedClinic,
          ClinicID: updatedClinic.ClinicID || clinicId,
        }));

        setClinic((prev) => ({
          ...(prev || { ClinicID: clinicId, Name: cleanName }),
          ClinicID: updatedClinic.ClinicID || clinicId,
          Name: updatedClinic.Name || updatedClinic.ClinicName || cleanName,
          AddressLine1: updatedClinic.AddressLine1 ?? prev?.AddressLine1 ?? null,
          AddressLine2: updatedClinic.AddressLine2 ?? prev?.AddressLine2 ?? null,
          PostalCode: updatedClinic.PostalCode ?? prev?.PostalCode ?? null,
          Province: updatedClinic.Province ?? prev?.Province ?? null,
          Phone: updatedClinic.Phone ?? prev?.Phone ?? null,
          PhoneExtension:
            updatedClinic.PhoneExtension ?? prev?.PhoneExtension ?? null,
          Fax: updatedClinic.Fax ?? prev?.Fax ?? null,
          AdministrationEmail:
            updatedClinic.AdministrationEmail ?? prev?.AdministrationEmail ?? null,
          Website: updatedClinic.Website ?? prev?.Website ?? null,
          Latitude: updatedClinic.Latitude ?? prev?.Latitude ?? null,
          Longitude: updatedClinic.Longitude ?? prev?.Longitude ?? null,
          CityID: updatedClinic.CityID ?? prev?.CityID ?? null,
          CityName: updatedClinic.CityName ?? prev?.CityName ?? null,
          CityRegionID: updatedClinic.CityRegionID ?? prev?.CityRegionID ?? null,
          CityRegionName:
            updatedClinic.CityRegionName ||
            updatedClinic.RegionName ||
            prev?.CityRegionName ||
            null,
          CreatedAt: updatedClinic.CreatedAt ?? prev?.CreatedAt ?? null,
          UpdatedAt: updatedClinic.UpdatedAt ?? prev?.UpdatedAt ?? null,
        }));

        setGovernanceStatus(
          String(
            updatedClinic.GovernanceStatus ||
              getFirstValue(updatedClinic || {}, ["governanceStatus"]) ||
              governanceStatus ||
              ""
          ).trim() || null
        );

        setClinicForm(buildClinicEditForm(updatedClinic, clinic));
      }

      setIsEditingClinic(false);
    } catch (e: any) {
      window.alert(e?.message || "Failed to update clinic.");
    } finally {
      setClinicSaving(false);
    }
  }

  async function setClinicGovernanceAction(action: "suspend" | "activate") {
    if (statusBusy) return;

    const clinicId = displayClinicId;
    if (!clinicId) {
      window.alert("ClinicID is missing.");
      return;
    }

    const actionLabel = action === "suspend" ? "Suspend" : "Activate";
    const confirmed = window.confirm(`${actionLabel} this clinic?`);
    if (!confirmed) return;

    const reason = window.prompt(
      "Enter governance reason:",
      action === "suspend"
        ? "Clinic suspended by Super Admin"
        : "Clinic activated by Super Admin"
    );

    if (reason === null) return;

    const cleanReason = reason.trim();
    if (cleanReason.length < 5) {
      window.alert("Governance reason must be at least 5 characters.");
      return;
    }

    setStatusBusy(true);

    try {
      const res = await fetch("/api/superadmin-clinic-set-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          clinicId,
          action,
          reason: cleanReason,
        }),
      });

      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        clinic?: SuperAdminClinic | null;
        newStatus?: string | null;
      };

      if (!res.ok) {
        throw new Error(
          data?.error || `Failed to ${action} clinic (${res.status})`
        );
      }

      const updatedClinic = data?.clinic || null;
      const nextStatus =
        updatedClinic?.GovernanceStatus ||
        data?.newStatus ||
        (action === "suspend" ? "Suspended" : "Approved");

      setGovernanceStatus(String(nextStatus || "").trim() || null);
      if (updatedClinic) {
        setSuperAdminClinic((prev) => ({
          ...(prev || {}),
          ...updatedClinic,
          ClinicID: updatedClinic.ClinicID || clinicId,
        }));
      }
    } catch (e: any) {
      window.alert(e?.message || `Failed to ${action} clinic.`);
    } finally {
      setStatusBusy(false);
    }
  }

  const addressLines = [
    superAdminClinic?.AddressLine1 || clinic?.AddressLine1 || "",
    superAdminClinic?.AddressLine2 || clinic?.AddressLine2 || "",
    [
      superAdminClinic?.CityName || clinic?.CityName || "",
      superAdminClinic?.Province || clinic?.Province || "",
      superAdminClinic?.PostalCode || clinic?.PostalCode || "",
    ]
      .filter(Boolean)
      .join(" "),
  ].filter(Boolean);


  const publicMetaCounts = [
    `${accessibilityFeatures.length} accessibility feature${
      accessibilityFeatures.length === 1 ? "" : "s"
    }`,
    `${onSiteServices.length} on-site service${
      onSiteServices.length === 1 ? "" : "s"
    }`,
    `${operatingHours.length} hour row${operatingHours.length === 1 ? "" : "s"}`,
    `${regionMappings.length} region mapping${
      regionMappings.length === 1 ? "" : "s"
    }`,
  ];

  return (
    <div className="sa-clinic-detail">
      <div className="sa-clinic-detail__topbar">
        <div className="sa-clinic-detail__titleWrap">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px 10px",
              borderRadius: 999,
              background: "#eaf4ff",
              border: "1px solid #cfe6ff",
              color: "#1669b3",
              fontSize: 12,
              fontWeight: 650,
              marginBottom: 10,
            }}
          >
            Clinic governance
          </div>

          <div className="sa-clinic-detail__titleRow">
            <h1 className="sa-clinic-detail__title">Clinic Details</h1>
            <StatusPill value={governanceStatus} />
          </div>

          <div className="sa-clinic-detail__subtitle">
            <div className="sa-clinic-detail__clinicName">
              {loading && govLoading ? "Loading…" : displayClinicName}
            </div>
            <div className="sa-clinic-detail__metaLine">
              {displayCityName}
              {displayRegionName ? ` · ${displayRegionName}` : ""}
              {displayClinicId ? ` · ClinicID: ${String(displayClinicId)}` : ""}
              {govLoading ? " · Loading governance…" : ""}
              {govErr ? " · Governance error" : ""}
            </div>
          </div>
        </div>

        <div className="sa-clinic-detail__actions">
          <button
            type="button"
            onClick={backToClinics}
            className="sa-clinic-detail__btn sa-clinic-detail__btn--secondary"
          >
            ← Back to Clinics
          </button>

          <button
            type="button"
            onClick={reloadPage}
            disabled={loading || govLoading || adminsLoading || statusBusy}
            className="sa-clinic-detail__btn sa-clinic-detail__btn--secondary"
          >
            Refresh
          </button>
        </div>
      </div>

      {(publicErr || govErr) && (
        <div className="sa-clinic-detail__alert">
          {publicErr ? <div>{publicErr}</div> : null}
          {govErr ? <div>{govErr}</div> : null}
        </div>
      )}

      <section className="sa-clinic-detail__summaryPanel">
        <div className="sa-clinic-detail__summaryBadges">
          <PillBadge
            label={`Governance: ${governanceStatus || "Not returned"}`}
            tone={getGovernanceTone(governanceStatus)}
          />

          <PillBadge
            label={
              publicEligibility.known
                ? publicEligibility.eligible
                  ? "Public listing ready"
                  : "Public listing needs repair"
                : "Public listing not returned"
            }
            tone={
              publicEligibility.known
                ? publicEligibility.eligible
                  ? "success"
                  : "warning"
                : "neutral"
            }
          />

          <PillBadge
            label={`${activeAdminCount} active admin${
              activeAdminCount === 1 ? "" : "s"
            }`}
            tone={activeAdminCount > 0 ? "success" : "warning"}
          />

          <PillBadge label="No patient PHI" tone="success" />
        </div>

        <div className="sa-clinic-detail__summaryText">
          Super Admin clinic detail view for governance, public listing safety,
          and Clinic Admin ownership. Patient request details and clinical
          information are not shown here.
        </div>

        {publicEligibility.known && !publicEligibility.eligible ? (
          <div className="sa-clinic-detail__warningLine">
            Missing public listing requirements:{" "}
            {publicEligibility.missing.join(", ")}
          </div>
        ) : null}
      </section>

      <div className="sa-clinic-detail__grid">
        <div className="sa-clinic-detail__leftCol">
          <Card title="Clinic Profile">
            <div className="sa-clinic-detail__profileHeader">
              <div>
                <div className="sa-clinic-detail__profileName">
                  {displayClinicName}
                </div>
                <div className="sa-clinic-detail__profileMeta">
                  {addressLines.length ? addressLines.join(" · ") : "Address not set"}
                </div>
              </div>

              <PublicListingPill eligibility={publicEligibility} />
            </div>

            <div className="sa-clinic-detail__formGrid">
              <Field label="Clinic name">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={isEditingClinic ? clinicForm.name : displayClinicName}
                  onChange={(e) => updateClinicField("name", e.target.value)}
                  placeholder="Clinic name"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Governance status">
                <input
                  disabled
                  value={governanceStatus || "—"}
                  placeholder="Governance status"
                  className="sa-clinic-detail__input"
                  readOnly
                />
              </Field>

              <Field label="Address line 1">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.addressLine1
                      : formatNullable(
                          superAdminClinic?.AddressLine1 ?? clinic?.AddressLine1
                        )
                  }
                  onChange={(e) =>
                    updateClinicField("addressLine1", e.target.value)
                  }
                  placeholder="Address line 1"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Address line 2">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.addressLine2
                      : formatNullable(
                          superAdminClinic?.AddressLine2 ?? clinic?.AddressLine2
                        )
                  }
                  onChange={(e) =>
                    updateClinicField("addressLine2", e.target.value)
                  }
                  placeholder="Address line 2"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="City">
                <input
                  disabled
                  value={formatNullable(
                    superAdminClinic?.CityName ?? clinic?.CityName
                  )}
                  placeholder="City"
                  className="sa-clinic-detail__input"
                  readOnly
                />
              </Field>

              <Field label="Region">
                <input
                  disabled
                  value={formatNullable(displayRegionName)}
                  placeholder="Region"
                  className="sa-clinic-detail__input"
                  readOnly
                />
              </Field>

              <Field label="Province">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.province
                      : formatNullable(
                          superAdminClinic?.Province ?? clinic?.Province
                        )
                  }
                  onChange={(e) => updateClinicField("province", e.target.value)}
                  placeholder="Province"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Postal code">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.postalCode
                      : formatNullable(
                          superAdminClinic?.PostalCode ?? clinic?.PostalCode
                        )
                  }
                  onChange={(e) =>
                    updateClinicField("postalCode", e.target.value)
                  }
                  placeholder="Postal code"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Phone">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.phone
                      : formatNullable(superAdminClinic?.Phone ?? clinic?.Phone)
                  }
                  onChange={(e) => updateClinicField("phone", e.target.value)}
                  placeholder="Phone"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Phone extension">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.phoneExtension
                      : formatNullable(
                          superAdminClinic?.PhoneExtension ??
                            clinic?.PhoneExtension
                        )
                  }
                  onChange={(e) =>
                    updateClinicField("phoneExtension", e.target.value)
                  }
                  placeholder="Phone extension"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Fax">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.fax
                      : formatNullable(superAdminClinic?.Fax ?? clinic?.Fax)
                  }
                  onChange={(e) => updateClinicField("fax", e.target.value)}
                  placeholder="Fax"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Admin email">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.administrationEmail
                      : formatNullable(
                          superAdminClinic?.AdministrationEmail ??
                            clinic?.AdministrationEmail
                        )
                  }
                  onChange={(e) =>
                    updateClinicField("administrationEmail", e.target.value)
                  }
                  placeholder="Admin email"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Website">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.website
                      : formatNullable(superAdminClinic?.Website ?? clinic?.Website)
                  }
                  onChange={(e) => updateClinicField("website", e.target.value)}
                  placeholder="Website"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Latitude">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.latitude
                      : formatNullable(
                          clinic?.Latitude ?? superAdminClinic?.Latitude
                        )
                  }
                  onChange={(e) => updateClinicField("latitude", e.target.value)}
                  placeholder="Latitude"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>

              <Field label="Longitude">
                <input
                  disabled={!isEditingClinic || clinicSaving}
                  value={
                    isEditingClinic
                      ? clinicForm.longitude
                      : formatNullable(
                          clinic?.Longitude ?? superAdminClinic?.Longitude
                        )
                  }
                  onChange={(e) =>
                    updateClinicField("longitude", e.target.value)
                  }
                  placeholder="Longitude"
                  className="sa-clinic-detail__input"
                  readOnly={!isEditingClinic}
                />
              </Field>
            </div>

            <div className="sa-clinic-detail__formFooter">
              {isEditingClinic ? (
                <>
                  <button
                    type="button"
                    onClick={saveClinicProfile}
                    disabled={clinicSaving || loading || govLoading}
                    className="sa-clinic-detail__btn sa-clinic-detail__btn--primary"
                  >
                    {clinicSaving ? "Saving…" : "Save changes"}
                  </button>
                  <button
                    type="button"
                    onClick={cancelClinicEdit}
                    disabled={clinicSaving}
                    className="sa-clinic-detail__btn sa-clinic-detail__btn--secondary"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={startClinicEdit}
                  disabled={loading || govLoading || clinicSaving || statusBusy}
                  className="sa-clinic-detail__btn sa-clinic-detail__btn--secondary"
                >
                  Edit clinic profile
                </button>
              )}
            </div>
          </Card>

          <div className="sa-clinic-detail__rightCol">
            <Card title="Governance">
              <div className="sa-clinic-detail__muted">
                {govErr ? (
                  <div className="sa-clinic-detail__placeholderNote">{govErr}</div>
                ) : (
                  <>
                    Current status:{" "}
                    <strong style={{ color: "#102a43" }}>
                      {governanceStatus || "Not returned"}
                    </strong>
                  </>
                )}
              </div>

              <div className="sa-clinic-detail__kvGrid">
                <Kv label="Approved at" value={fmtDate(superAdminClinic?.ApprovedAt)} />
                <Kv label="Approved by" value={approvedBy} />
                <Kv
                  label="Created"
                  value={fmtDate(superAdminClinic?.CreatedAt || clinic?.CreatedAt)}
                />
                <Kv
                  label="Updated"
                  value={fmtDate(superAdminClinic?.UpdatedAt || clinic?.UpdatedAt)}
                />
              </div>

              {lifecycleMeta.length ? (
                <div className="sa-clinic-detail__smallMeta">
                  {lifecycleMeta.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
              ) : null}

              <div className="sa-clinic-detail__btnRow" style={{ marginTop: 14 }}>
                {canSuspendClinic ? (
                  <button
                    type="button"
                    onClick={() => setClinicGovernanceAction("suspend")}
                    disabled={statusBusy || govLoading}
                    className="sa-clinic-detail__btn sa-clinic-detail__btn--danger"
                  >
                    {statusBusy ? "Working…" : "Suspend clinic"}
                  </button>
                ) : null}

                {canActivateClinic ? (
                  <button
                    type="button"
                    onClick={() => setClinicGovernanceAction("activate")}
                    disabled={statusBusy || govLoading}
                    className="sa-clinic-detail__btn sa-clinic-detail__btn--primary"
                  >
                    {statusBusy ? "Working…" : "Activate clinic"}
                  </button>
                ) : null}
              </div>
            </Card>

            <Card title="Public Listing Requirements">
              <div className="sa-clinic-detail__muted">
                Approved clinics should have complete public listing data before
                appearing in public search.
              </div>

              <div className="sa-clinic-detail__checks">
                {renderCheck("Clinic name", hasValue(displayClinicName))}
                {renderCheck(
                  "Address",
                  hasValue(superAdminClinic?.AddressLine1 ?? clinic?.AddressLine1)
                )}
                {renderCheck(
                  "City",
                  hasValue(superAdminClinic?.CityID) ||
                    hasValue(superAdminClinic?.CityName) ||
                    hasValue(clinic?.CityID) ||
                    hasValue(clinic?.CityName)
                )}
                {renderCheck(
                  "Postal code",
                  hasValue(superAdminClinic?.PostalCode ?? clinic?.PostalCode)
                )}
                {renderCheck(
                  "Phone",
                  hasValue(superAdminClinic?.Phone ?? clinic?.Phone)
                )}
                {renderCheck(
                  "Coordinates",
                  hasValue(clinic?.Latitude ?? superAdminClinic?.Latitude) &&
                    hasValue(clinic?.Longitude ?? superAdminClinic?.Longitude)
                )}
              </div>
            </Card>

            <Card title="Public Profile Data">
              <div className="sa-clinic-detail__muted">
                Public-facing clinic metadata currently returned by the clinic
                detail endpoint.
              </div>

              <div className="sa-clinic-detail__chipList">
                {publicMetaCounts.map((item) => (
                  <span key={item} className="sa-clinic-detail__chip">
                    {item}
                  </span>
                ))}
              </div>
            </Card>
          </div>

          <Card title="Clinic Admins">
            <div className="sa-clinic-detail__sectionHeader">
              <div>
                <div className="sa-clinic-detail__muted">
                  Active Clinic Admins govern the clinic profile, providers, and
                  clinic workflow access.
                </div>
              </div>
              <PillBadge
                label={`${activeAdminCount} active`}
                tone={activeAdminCount > 0 ? "success" : "warning"}
              />
            </div>

            {adminsErr ? (
              <div className="sa-clinic-detail__placeholderNote">{adminsErr}</div>
            ) : adminsLoading ? (
              <div className="sa-clinic-detail__placeholderNote">
                Loading Clinic Admins…
              </div>
            ) : admins.length === 0 ? (
              <div className="sa-clinic-detail__placeholderNote">
                No Clinic Admin roles returned for this clinic.
              </div>
            ) : (
              <div className="sa-clinic-detail__adminList">
                {admins.map((a, index) => {
                  const roleId = a.UserRoleID ?? `${a.UserID || "admin"}-${index}`;
                  const active = isRoleActive(a.IsActive);

                  return (
                    <div
                      key={String(roleId)}
                      className={`sa-clinic-detail__adminRow ${
                        active ? "" : "sa-clinic-detail__adminRow--inactive"
                      }`}
                    >
                      <div>
                        <div className="sa-clinic-detail__adminName">
                          {a.DisplayName || a.Email || `User #${a.UserID || "—"}`}
                          <span
                            className={`sa-clinic-detail__adminBadge ${
                              active
                                ? "sa-clinic-detail__adminBadge--active"
                                : "sa-clinic-detail__adminBadge--inactive"
                            }`}
                          >
                            {active ? "Active" : "Inactive"}
                          </span>
                        </div>

                        <div className="sa-clinic-detail__adminEmail">
                          {a.Email || "—"}
                        </div>

                        <div className="sa-clinic-detail__adminEmail">
                          Assigned: {fmtDate(a.AssignedAt)} · Deactivated:{" "}
                          {fmtDate(a.DeactivatedAt)}
                        </div>
                        <div className="sa-clinic-detail__adminEmail">
                          User status: {a.UserStatus || "—"} · Auth:{" "}
                          {a.AuthProvider || "—"}
                        </div>
                      </div>

                      <div className="sa-clinic-detail__adminId">#{roleId}</div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="sa-clinic-detail__btnRow" style={{ marginTop: 12 }}>
              <button
                type="button"
                disabled
                title="Managed outside this view"
                className="sa-clinic-detail__btn sa-clinic-detail__btn--disabled"
              >
                + Add admin
              </button>
              <button
                type="button"
                disabled
                title="Managed outside this view"
                className="sa-clinic-detail__btn sa-clinic-detail__btn--disabled"
              >
                Remove selected
              </button>
            </div>
          </Card>

          <Card title="Audit">
            <div className="sa-clinic-detail__placeholderNote">
              Governance actions are written to the Activity Log. Clinic-specific
              filtering can be added when supported by the activity endpoint.
            </div>

            <div style={{ marginTop: 12 }}>
              <Link
                to="/portal/super-admin/activity"
                className="sa-clinic-detail__btn sa-clinic-detail__btn--link"
              >
                Open Activity Log →
              </Link>
            </div>
          </Card>

          <Card title="Additional Platform Areas">
            <div className="sa-clinic-detail__muted">
              These areas are managed outside the current clinic detail view:
              <div className="sa-clinic-detail__bullet" style={{ marginTop: 8 }}>
                • Provider repair workflows
              </div>
              <div className="sa-clinic-detail__bullet">
                • Advanced clinic analytics
              </div>
              <div className="sa-clinic-detail__bullet">
                • Billing or subscription management
              </div>
              <div className="sa-clinic-detail__bullet">
                • Public profile editing from Super Admin
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ---------- Local UI components ---------- */

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="sa-clinic-detail__card">
      <div className="sa-clinic-detail__cardTitle">{title}</div>
      {children}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="sa-clinic-detail__field">
      <span className="sa-clinic-detail__label">{label}</span>
      {children}
    </label>
  );
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div className="sa-clinic-detail__kv">
      <div className="sa-clinic-detail__kvLabel">{label}</div>
      <div className="sa-clinic-detail__kvValue">{value}</div>
    </div>
  );
}

function StatusPill({ value }: { value: string | null }) {
  const v = String(value || "").trim();
  const lower = v.toLowerCase();

  let cls = "sa-clinic-detail__statusPill";
  if (!v) cls += " sa-clinic-detail__statusPill--empty";
  else if (lower === "approved") cls += " sa-clinic-detail__statusPill--approved";
  else if (lower === "suspended" || lower === "deactivated")
    cls += " sa-clinic-detail__statusPill--suspended";
  else cls += " sa-clinic-detail__statusPill--other";

  return <span className={cls}>{v || "—"}</span>;
}

function PillBadge({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: BadgeTone;
}) {
  const styles: Record<
    BadgeTone,
    { bg: string; bd: string; fg: string; dot: string }
  > = {
    success: {
      bg: "rgba(22, 163, 74, 0.10)",
      bd: "rgba(22, 163, 74, 0.25)",
      fg: "#166534",
      dot: "#16a34a",
    },
    danger: {
      bg: "rgba(185, 28, 28, 0.08)",
      bd: "rgba(185, 28, 28, 0.22)",
      fg: "#991b1b",
      dot: "#dc2626",
    },
    warning: {
      bg: "#fffaf0",
      bd: "#f4d18f",
      fg: "#92400e",
      dot: "#b45309",
    },
    neutral: {
      bg: "#f8fafc",
      bd: "#e2e8f0",
      fg: "#64748b",
      dot: "#94a3b8",
    },
    info: {
      bg: "#eaf4ff",
      bd: "#cfe6ff",
      fg: "#1669b3",
      dot: "#1669b3",
    },
  };

  const s = styles[tone];

  return (
    <span
      className="sa-clinic-detail__pillBadge"
      style={{
        borderColor: s.bd,
        background: s.bg,
        color: s.fg,
      }}
      title={label}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: s.dot,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

function PublicListingPill({
  eligibility,
}: {
  eligibility: PublicEligibilityResult;
}) {
  if (!eligibility.known) {
    return <PillBadge label="Public listing not returned" tone="neutral" />;
  }

  if (eligibility.eligible) {
    return <PillBadge label="Public listing ready" tone="success" />;
  }

  return <PillBadge label="Public listing needs repair" tone="warning" />;
}

function renderCheck(label: string, ok: boolean) {
  return (
    <div className="sa-clinic-detail__checkRow" key={label}>
      <span
        className={`sa-clinic-detail__checkIcon ${
          ok ? "sa-clinic-detail__checkIcon--ok" : "sa-clinic-detail__checkIcon--bad"
        }`}
        aria-hidden="true"
      >
        {ok ? "✓" : "!"}
      </span>
      <span>{label}</span>
    </div>
  );
}

function getGovernanceTone(value: string | null): BadgeTone {
  const lower = String(value || "").trim().toLowerCase();
  if (lower === "approved") return "success";
  if (lower === "suspended" || lower === "deactivated") return "warning";
  if (lower === "pending") return "info";
  return "neutral";
}

function fmtDate(val: string | null | undefined) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatNullable(value: unknown) {
  const s = String(value ?? "").trim();
  return s || "—";
}

function hasValue(value: unknown) {
  return String(value ?? "").trim().length > 0;
}

function asNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getFirstValue(row: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

function isRoleActive(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return String(value).trim().toLowerCase() === "true";
}

function editValue(value: unknown) {
  const s = String(value ?? "").trim();
  return s === "—" ? "" : s;
}

function cleanNullable(value: string) {
  const s = String(value ?? "").trim();
  return s.length ? s : null;
}

function buildClinicEditForm(
  superAdminClinic: SuperAdminClinic | null,
  clinic: ClinicPublic | null
): ClinicEditForm {
  return {
    name: editValue(
      superAdminClinic?.ClinicName || superAdminClinic?.Name || clinic?.Name
    ),
    addressLine1: editValue(
      superAdminClinic?.AddressLine1 ?? clinic?.AddressLine1
    ),
    addressLine2: editValue(
      superAdminClinic?.AddressLine2 ?? clinic?.AddressLine2
    ),
    province: editValue(superAdminClinic?.Province ?? clinic?.Province),
    postalCode: editValue(
      superAdminClinic?.PostalCode ?? clinic?.PostalCode
    ),
    phone: editValue(superAdminClinic?.Phone ?? clinic?.Phone),
    phoneExtension: editValue(
      superAdminClinic?.PhoneExtension ?? clinic?.PhoneExtension
    ),
    fax: editValue(superAdminClinic?.Fax ?? clinic?.Fax),
    administrationEmail: editValue(
      superAdminClinic?.AdministrationEmail ?? clinic?.AdministrationEmail
    ),
    website: editValue(superAdminClinic?.Website ?? clinic?.Website),
    latitude: editValue(superAdminClinic?.Latitude ?? clinic?.Latitude),
    longitude: editValue(superAdminClinic?.Longitude ?? clinic?.Longitude),
  };
}

function getPublicEligibility(
  row: ClinicPublic | SuperAdminClinic | null
): PublicEligibilityResult {
  if (!row) return { known: false, eligible: false, missing: [] };

  const explicit = getFirstValue(row as Record<string, unknown>, [
    "IsPublicEligible",
    "isPublicEligible",
    "PublicEligible",
    "publicEligible",
  ]);

  if (explicit !== undefined) {
    const eligible = explicit === true || explicit === 1 || String(explicit).trim().toLowerCase() === "true";
    return { known: true, eligible, missing: eligible ? [] : ["Public eligibility"] };
  }

  const hasAnyPublicField =
    "AddressLine1" in row ||
    "addressLine1" in row ||
    "PostalCode" in row ||
    "postalCode" in row ||
    "Phone" in row ||
    "phone" in row ||
    "Latitude" in row ||
    "latitude" in row ||
    "Longitude" in row ||
    "longitude" in row;

  if (!hasAnyPublicField) {
    return { known: false, eligible: false, missing: [] };
  }

  const missing: string[] = [];

  if (!hasValue(getFirstValue(row as Record<string, unknown>, ["AddressLine1", "addressLine1"]))) {
    missing.push("Address");
  }

  if (
    !hasValue(getFirstValue(row as Record<string, unknown>, ["CityID", "cityId"])) &&
    !hasValue(getFirstValue(row as Record<string, unknown>, ["CityName", "cityName"]))
  ) {
    missing.push("City");
  }

  if (!hasValue(getFirstValue(row as Record<string, unknown>, ["PostalCode", "postalCode"]))) {
    missing.push("Postal code");
  }

  if (!hasValue(getFirstValue(row as Record<string, unknown>, ["Phone", "phone"]))) {
    missing.push("Phone");
  }

  if (
    !hasValue(getFirstValue(row as Record<string, unknown>, ["Latitude", "latitude"])) ||
    !hasValue(getFirstValue(row as Record<string, unknown>, ["Longitude", "longitude"]))
  ) {
    missing.push("Coordinates");
  }

  return {
    known: true,
    eligible: missing.length === 0,
    missing,
  };
}